import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { catchError, map, Observable, of } from 'rxjs';

import { PagedResponse } from '@ninety/platform/models/_shared/paged-response';
import { Commitment } from '@ninety/platform/models/feedback/commitments';
import { Conversation, ListViewConversation } from '@ninety/platform/models/feedback/conversation';
import { ConversationManagee } from '@ninety/platform/models/feedback/conversation-managee';
import {
  FormalConversationQuestion,
  DefaultConversationQuestion,
} from '@ninety/platform/models/feedback/formal-conversation-question';
import { NewConversation } from '@ninety/platform/models/feedback/new-conversation';
import { QueryParamsService, extractValueFromStore } from '@ninety/platform/services';
import { selectLanguage } from '@ninety/platform/state';

import { FeedbackApiActions as actions } from './feedback-api.actions';
import { ConversationEditRequest } from './models/conversation-edit-request';
import { ConversationListRequestParams } from './models/conversation-list-request.model';

@Injectable({ providedIn: 'root' })
export class FeedbackApiService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);

  conversationsApi = '/api/v4/Conversations';
  companies = '/api/v4/Companies';

  getConversation(id: string) {
    return this.http.get<Conversation>(`${this.conversationsApi}/${id}`);
  }

  getConversations(config: ConversationListRequestParams): Observable<Action> {
    const params = QueryParamsService.build(
      {
        isCompleted: config.isCompleted,
        searchText: config.searchText,
        page: config.page,
        take: config.take,
        sortField: config.sort.active,
        sortDirection: config.sort.direction === 'asc' ? 'ASC' : 'DESC',
        type: config.conversationType,
        selectedUserId: config.selectedUserId ?? 'all',
      },
      true
    );
    const language = extractValueFromStore(this.store, selectLanguage);
    return this.http.get<PagedResponse<ListViewConversation>>(this.conversationsApi, { params }).pipe(
      map(conversations => actions.conversationListReceived({ conversations })),
      catchError((error: unknown) =>
        of(
          actions.unableToRetrieveConversationList({
            error,
            customMessage: `Could not get ${language?.feedback?.items}. Please try again.`,
          })
        )
      )
    );
  }

  createConversations(
    managees: ConversationManagee[],
    conversation: Conversation,
    selectedUserId: string,
    createTodos?: boolean
  ): Observable<Action> {
    const body: NewConversation = new NewConversation(conversation, managees, createTodos);
    return this.http.post<Conversation[]>(this.conversationsApi, body).pipe(
      map(newConversations => {
        const newConversationsForUser = newConversations?.filter(
          (c: Conversation) =>
            selectedUserId === c.manageeUserId || selectedUserId === c.managerUserId || selectedUserId === 'all'
        );
        return actions.conversationCreated({ conversations: [...newConversationsForUser], createTodos });
      }),
      catchError((error: unknown) => {
        const language = extractValueFromStore(this.store, selectLanguage);
        return of(
          actions.unableToCreateConversation({
            error,
            customMessage: `Could not create all ${language?.feedback?.items}.Please try again.`,
          })
        );
      })
    );
  }

  updateConversationStatus(conversationId: string, isCompleted: boolean): Observable<Action> {
    return this.patchConversation(conversationId, { isCompleted }).pipe(
      map(conversation => actions.conversationStatusUpdated({ conversation })),
      catchError((error: unknown) => {
        const language = extractValueFromStore(this.store, selectLanguage);
        return of(
          actions.unableToUpdateConversationStatus({
            error,
            customMessage: `Could not update ${language?.feedback?.item} status. Please try again.`,
          })
        );
      })
    );
  }

  deleteConversation(conversationId: string): Observable<Action> {
    return this.patchConversation(conversationId, { isDeleted: true }).pipe(
      map(conversation => actions.conversationDeleted({ conversation })),
      catchError((error: unknown) => {
        const language = extractValueFromStore(this.store, selectLanguage);
        return of(
          actions.unableToDeleteConversation({
            error,
            customMessage: `Could not delete ${language?.feedback?.item}. Please try again.`,
          })
        );
      })
    );
  }

  editConversation(conversationId: string, update: ConversationEditRequest): Observable<Action> {
    return this.patchConversation(conversationId, update).pipe(
      map(conversation => actions.conversationSuccessfullyEdited({ conversation })),
      catchError((error: unknown) => {
        const language = extractValueFromStore(this.store, selectLanguage);
        return of(
          actions.unableToEditConversation({
            error,
            customMessage: `Could not edit ${language?.feedback?.item}. Please try again.`,
          })
        );
      })
    );
  }

  updateQuarterlyQuestions(
    quarterlyQuestions: DefaultConversationQuestion[],
    annualQuestions: FormalConversationQuestion[],
    leadershipCommitments: Commitment[],
    coachingCommitments: Commitment[]
  ): Observable<Action> {
    return this.patchConversationSettings(
      quarterlyQuestions,
      annualQuestions,
      leadershipCommitments,
      coachingCommitments
    ).pipe(
      map(quarterlyQuestions => actions.quarterlyQuestionsSuccesfullyUpdated({ quarterlyQuestions })),
      catchError((error: unknown) =>
        of(
          actions.unableToUpdateQuarterlyQuestions({
            error,
            customMessage: 'Could not update quarterly questions. Please try again.',
          })
        )
      )
    );
  }

  // deprecated method
  patchConversation(conversationId: string, update: Partial<Conversation>) {
    return this.http.patch<Conversation>(`${this.conversationsApi}/${conversationId}`, update);
  }

  private patchConversationSettings(
    quarterlyQuestions: FormalConversationQuestion[],
    annualQuestions: FormalConversationQuestion[],
    leadershipCommitments: Commitment[],
    coachingCommitments: Commitment[]
  ) {
    const update = {
      quarterlyQuestions: quarterlyQuestions,
      annualQuestions: annualQuestions,
      leadershipCommitments: leadershipCommitments,
      coachingCommitments: coachingCommitments,
    };
    return this.http.put<FormalConversationQuestion[]>(`${this.companies}/Settings/OneOnOne`, update);
  }
}
